import { type TDisplayColumnData } from 'components/data/types';
import { type TDataTableContext } from 'components/DataTable/types';
import { type StorageKey, getItem, storageManager } from 'utils/storage';

import { type TExtensionColumn } from '../types';

export const columnPickerStorage = ({ storageKey }: { storageKey?: StorageKey }) => {
  const customerId = getItem('customerId');
  if (storageKey && customerId) {
    const key = `${storageKey}:${customerId}`;
    return storageManager({ storageKey: key });
  }

  return storageManager({ storageKey: undefined });
};

const getColumnKey = (column: TDisplayColumnData) => (column.key ? column.key : column.dataIndex);

// Columns added after the set was saved are appended hidden, so the column picker lists them
// without changing the user's saved layout. Untitled columns aren't in the picker, so skip them.
const mapExtensionColumnsToDefinition = (
  columnDefs: TDisplayColumnData[],
  savedColumns: TExtensionColumn[]
): (TDisplayColumnData & { visible: boolean })[] => {
  const newColumns = columnDefs
    .filter(column => column.title && !savedColumns.some(({ key }) => key === getColumnKey(column)))
    .map(column => ({ ...column, visible: false }));

  return savedColumns
    .map((column: TExtensionColumn) => {
      const columnDefinition = columnDefs.find(
        implementation => getColumnKey(implementation) === column.key
      );
      if (columnDefinition) {
        return {
          ...columnDefinition,
          visible: column.visible,
        };
      }
      return null;
    })
    .filter((c): c is TDisplayColumnData & { visible: boolean } => !!c)
    .concat(newColumns);
};

export const updateCustomCols = (
  columnDefs: TDisplayColumnData[],
  customColumns: TExtensionColumn[],
  updateCustomizedColumnSet: TDataTableContext['updateCustomizedColumnSet'],
  columnPickerStorageManager: any,
  isInitialRender?: boolean
) => {
  const columnsToSave: Pick<TExtensionColumn, 'key' | 'visible'>[] = (customColumns ?? []).map(
    ({ key, visible }) => ({ key, visible })
  );
  if (customColumns?.length || isInitialRender) {
    const columnDefinitionsWithVisibility = mapExtensionColumnsToDefinition(
      columnDefs,
      customColumns
    );
    updateCustomizedColumnSet(columnDefinitionsWithVisibility);
    columnPickerStorageManager.set(columnsToSave);
  } else {
    updateCustomizedColumnSet(undefined);
    columnPickerStorageManager.remove();
  }
};
